import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/store/auth.store';
import {
  ForgotPasswordData,
  LoginCredentials,
  RegisterData,
  RegisterFromInvitationData,
  ResetPasswordData,
  TwoFactorDisableData,
  TwoFactorVerifyData,
} from '@/types/auth.types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { autoInitPendingVault } from '@/utils/autoInitVault';

/**
 * Connexion + initialisation automatique du coffre en attente, avec le mot de
 * passe saisi (voir autoInitPendingVault).
 *
 * [AUTO-INIT VAULT AU LOGIN 2026-07] L'auto-init est fait ICI, dans l'unique
 * hook de connexion, et non dans une variante optionnelle : un second hook
 * "sans auto-init" finirait par être réutilisé et recréerait le blocage que la
 * décision du 10/05 ("plus aucun salarié ne doit être bloqué") supprime.
 *
 * Le mot de passe n'est utilisé qu'en mémoire pour dériver la clé : il n'est ni
 * stocké ni envoyé au serveur.
 */
export const useLogin = (
  onTwoFactorRequired?: (tempToken: string, password: string) => void,
  onOtpRequired?: (sessionId: string, password: string) => void
) => {
  const { t } = useTranslation();
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      authService.login(credentials),
    onSuccess: async (response, credentials) => {
      // 2FA (TOTP) requise : le mot de passe est transmis au formulaire suivant
      // pour pouvoir auto-initialiser le coffre une fois le code validé.
      if (response.data.requires2fa && response.data.sessionId) {
        onTwoFactorRequired?.(response.data.sessionId, credentials.password);
        return;
      }

      // OTP (code email) requis : idem.
      if (response.data.requiresOtp && response.data.sessionId) {
        onOtpRequired?.(response.data.sessionId, credentials.password);
        return;
      }

      setUser(response.data.user);
      setTokens(response.data.tokens);
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      toast.success(t('auth.messages.login.success'));

      await autoInitPendingVault(response.data.user, credentials.password);
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.login.error');
      toast.error(message);
    },
  });
};

export const useRegister = () => {
  const { t } = useTranslation();
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (data: RegisterData) => authService.register(data),
    onSuccess: response => {
      setUser(response.data.user);
      setTokens(response.data.tokens);
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      toast.success(t('auth.messages.register.success'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.register.error');
      toast.error(message);
    },
  });
};

export const useRegisterFromInvitation = () => {
  const { t } = useTranslation();
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (data: RegisterFromInvitationData) =>
      authService.registerFromInvitation(data),
    onSuccess: response => {
      setUser(response.data.user);
      setTokens(response.data.tokens);
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      toast.success(t('auth.messages.register.success'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.register.error');
      toast.error(message);
    },
  });
};

export const useForgotPassword = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ForgotPasswordData) => authService.forgotPassword(data),
    onSuccess: () => {
      toast.success(t('auth.messages.forgotPassword.success'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.forgotPassword.error');
      toast.error(message);
    },
  });
};

export const useResetPassword = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ResetPasswordData) => authService.resetPassword(data),
    onSuccess: () => {
      toast.success(t('auth.messages.resetPassword.success'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.resetPassword.error');
      toast.error(message);
    },
  });
};

export const useLogout = () => {
  const { t } = useTranslation();
  const { logout } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      // Le service logout gère déjà la suppression des tokens
      await logout();
    },
    onSuccess: () => {
      toast.success(t('auth.messages.logout.success'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message || t('auth.messages.logout.local');
      toast.info(message);
    },
  });
};

export const useProfile = (enabled: boolean = true) => {
  const { t } = useTranslation();
  const { setUser } = useAuthStore();

  return useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        const response = await authService.getProfile();
        setUser(response.data);
        return response;
      } catch (cause) {
        const error = cause as HttpApiError;
        const message =
          error?.response?.data?.message || t('auth.messages.profile.error');
        toast.error(message);
        throw error;
      }
    },
    enabled,
    retry: false,
  });
};

export const useRefreshToken = () => {
  const { t } = useTranslation();
  const { setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (refreshToken: string) =>
      authService.refreshToken(refreshToken),
    onSuccess: response => {
      setTokens(response.data.tokens);
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message || t('auth.messages.session.expired');
      toast.error(message);
    },
  });
};

/**
 * Hook pour initialiser l'authentification au démarrage de l'application
 * Charge automatiquement le profil utilisateur si un token est présent
 */
// Handle impersonation token synchronously before any render
function handleImpersonationToken(): boolean {
  const params = new URLSearchParams(window.location.search);
  const impersonateToken = params.get('impersonate');
  if (impersonateToken) {
    localStorage.setItem('accessToken', impersonateToken);
    // Set a dummy refresh token so isAuthenticated() returns true
    localStorage.setItem('refreshToken', 'impersonated');
    localStorage.setItem('tokenType', 'Bearer');
    // Clean up the URL
    const url = new URL(window.location.href);
    url.searchParams.delete('impersonate');
    window.history.replaceState({}, '', url.pathname + url.search);
    return true;
  }
  return false;
}

// Run synchronously at module load time (before any component renders)
handleImpersonationToken();

export const useAuthInit = () => {
  const { setLoading, setUser, setTokens } = useAuthStore();

  // Charge le profil seulement si un token est présent
  const hasToken = authService.isAuthenticated();
  const { data, isLoading, error } = useProfile(hasToken);

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);

      try {
        // Utiliser tokenManager pour récupérer les tokens stockés
        const storedTokens = authService.getAccessToken() ? true : false;

        if (storedTokens) {
          // Les tokens sont déjà gérés par le tokenManager
          // Le profil sera chargé automatiquement par useProfile
          // car hasToken sera true
        } else {
          // Pas de tokens, s'assurer que le store est vide
          setUser(null);
          setTokens(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // En cas d'erreur, nettoyer les tokens
        authService.clearAuth();
        setUser(null);
        setTokens(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [setLoading, setUser, setTokens]);

  return {
    isLoading: isLoading,
    isAuthenticated: hasToken && !error,
    user: data?.data,
  };
};

// ===== 2FA Hooks =====

export const useSetupTwoFactor = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => authService.setupTwoFactor(),
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.twoFactor.setupError');
      toast.error(message);
    },
  });
};

export const useEnableTwoFactor = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: TwoFactorVerifyData) =>
      authService.enableTwoFactor(data),
    onSuccess: () => {
      toast.success(t('auth.messages.twoFactor.enableSuccess'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.twoFactor.enableError');
      toast.error(message);
    },
  });
};

/**
 * @param loginPassword mot de passe saisi à la connexion (voir useVerifyOtp).
 */
export const useVerifyTwoFactor = (loginPassword?: string) => {
  const { t } = useTranslation();
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (data: TwoFactorVerifyData) =>
      authService.verifyTwoFactor(data),
    onSuccess: async response => {
      setUser(response.data.user);
      setTokens(response.data.tokens);
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      toast.success(t('auth.messages.twoFactor.verifySuccess'));

      if (loginPassword) {
        await autoInitPendingVault(response.data.user, loginPassword);
      }
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.twoFactor.verifyError');
      toast.error(message);
    },
  });
};

export const useDisableTwoFactor = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: TwoFactorDisableData) =>
      authService.disableTwoFactor(data),
    onSuccess: () => {
      toast.success(t('auth.messages.twoFactor.disableSuccess'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.twoFactor.disableError');
      toast.error(message);
    },
  });
};

/**
 * Check if 2FA setup is pending for vault users
 */
export const useCheckPending2FA = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['pending-2fa'],
    queryFn: () => authService.checkPending2FA(),
    enabled,
    retry: false,
  });
};

/**
 * Complete 2FA setup and clear the requires2FASetup flag
 */
export const useComplete2FASetup = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();

  return useMutation({
    mutationFn: () => authService.complete2FASetup(),
    onSuccess: () => {
      // Update the user in the store to reflect 2FA is no longer required
      // twoFactorEnabled is only true for TOTP, not for OTP (email-based)
      if (user) {
        setUser({
          ...user,
          requires2FASetup: false,
          twoFactorEnabled: user.securityMethod === 'totp',
        });
      }
      toast.success(t('auth.messages.twoFactor.setupComplete'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.twoFactor.setupError');
      toast.error(message);
    },
  });
};

// ===== OTP Hooks =====

/**
 * @param loginPassword mot de passe saisi à la connexion, transmis par
 * LoginForm. Permet d'auto-initialiser le coffre en attente une fois le code
 * validé (voir autoInitPendingVault). Non transmis au serveur.
 */
export const useVerifyOtp = (loginPassword?: string) => {
  const { t } = useTranslation();
  const { setUser, setTokens } = useAuthStore();

  return useMutation({
    mutationFn: (data: { sessionId: string; code: string }) =>
      authService.verifyOtp(data),
    onSuccess: async response => {
      setUser(response.data.user);
      setTokens(response.data.tokens);
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      toast.success(t('auth.messages.otp.verifySuccess'));

      if (loginPassword) {
        await autoInitPendingVault(response.data.user, loginPassword);
      }
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.otp.verifyError');
      toast.error(message);
    },
  });
};

export const useResendOtp = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (sessionId: string) => authService.resendOtp(sessionId),
    onSuccess: () => {
      toast.success(t('auth.messages.otp.resendSuccess'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.otp.resendError');
      toast.error(message);
    },
  });
};

export const useSetSecurityMethod = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();

  return useMutation({
    mutationFn: (method: 'totp' | 'otp' | null) =>
      authService.setSecurityMethod(method),
    onSuccess: (_response, method) => {
      if (user) {
        setUser({
          ...user,
          securityMethod: method,
        });
      }
      toast.success(t('auth.messages.securityMethod.success'));
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('auth.messages.securityMethod.error');
      toast.error(message);
    },
  });
};
